import { FatalError, ErrorCode, emitSystemError } from '@imprivata-cloud/authn';
import {
  createSpanName,
  debug,
  instrument,
  SpanComponent,
} from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/dataprivacy';
import type { UiCaptureCredentialsActionData } from '../store/types';
import { getCredentials } from '../api/appAccessWebService';
import { storeCodingContext } from './codingContextStorage';
import { CredentialsGetResponseNextAction } from '../shared/types/api';
import { sendAuthnDataToAgent, showWindow } from '../agent-api';
import { ShowWindowOption } from '../agent-api/types';
import { getWorkflowId } from '../tracing';
import { SpanNames } from '../tracing/constants';

export interface GetCredentialsServiceProps {
  codingContext?: CodingContext;
  oidcAuthorizationCode?: string;
}

/**
 * @returns UiCaptureCredentialsActionData or boolean to represent a saml authentication
 */
export const getCredentialsService = instrument(
  createSpanName(SpanComponent.UI, SpanNames.GET_USER_APP_CREDENTIALS),
  async ({
    codingContext,
    oidcAuthorizationCode,
  }: GetCredentialsServiceProps): Promise<
    UiCaptureCredentialsActionData | boolean
  > => {
    debug('[getCredentialsService]');

    if (!codingContext) {
      debug('[getCredentialsService] No coding context found');
      throw emitSystemError({
        code: ErrorCode.INTERNAL,
        message: 'No coding context ',
      });
    }

    const response = await getCredentials();

    debug('[getCredentialsService] Response', {
      ...response,
      credentialData: !!response.credentialData,
    });

    switch (response.nextAction) {
      case CredentialsGetResponseNextAction.ENROLL_USER: {
        // If we have a url, redirect
        if (response?.epicAuthorizationUrl) {
          // The redirect wipes in-memory state; stash the coding context so
          // the same one can be used on the redirect back.
          storeCodingContext(codingContext);

          const workflowId = getWorkflowId();
          debug('[getCredentialsService] Redirecting to Epic', { workflowId });
          localStorage.setItem('workflowId', workflowId);

          // The redirect unloads this page, so the show must reach the agent first.
          await showWindow(ShowWindowOption.show);
          // Actual redirect to epic
          window.location.href = response.epicAuthorizationUrl;
        }

        return true;
      }
      case CredentialsGetResponseNextAction.VERIFY_CREDENTIALS:
        if (response.credentialData?.data) {
          debug('[getCredentialsService] credential data present');
          const decryptedCredentialData = codingContext.decryptToJson(
            response.credentialData,
          ) as CredentialData;

          debug('[getCredentialsService] passing credential data to agent');
          sendAuthnDataToAgent(decryptedCredentialData, oidcAuthorizationCode);

          return !!decryptedCredentialData?.saml;
        } else {
          debug('[getCredentialsService::error] no credential data found');
          throw new FatalError({
            code: ErrorCode.INTERNAL,
            message: 'No credential data',
          });
        }
      case CredentialsGetResponseNextAction.CAPTURE_CREDENTIALS:
      case CredentialsGetResponseNextAction.CAPTURE_PASSWORD: {
        const isPwCaptureOnly =
          response.nextAction ===
          CredentialsGetResponseNextAction.CAPTURE_PASSWORD;
        debug('[getCredentialsService] password capture? ', isPwCaptureOnly);
        return {
          isPasswordCaptureOnly: isPwCaptureOnly,
          usernameHint: response.usernameHint || '',
          aadUpn: response.aadUpn || '',
        };
      }
      default:
        throw new FatalError({
          code: ErrorCode.INTERNAL,
          message: 'Unknown next-action',
        });
    }
  },
);
